const DISCONNECT_SUPPRESSION_MS = 15000;

/**
 * Хранит короткоживущее состояние перехода страницы через Back-Forward Cache.
 *
 * Chrome закрывает WebSocket при помещении страницы в BFCache. Tinode SDK
 * трактует такое закрытие как пользовательский disconnect с кодом 418, а
 * некоторые отложенные promise могут отдать эту ошибку уже после возврата
 * страницы. Guard отделяет этот ожидаемый служебный disconnect от настоящих
 * сетевых ошибок.
 */
export default class BFCacheGuard {
  constructor() {
    this.suspended = false;
    this.resumeInProgress = false;
    this.suppressDisconnectUntil = 0;
  }

  /**
   * Отмечает начало ухода страницы в BFCache.
   */
  suspend() {
    this.suspended = true;
    this.resumeInProgress = true;
    this.suppressExpectedDisconnects();
  }

  /**
   * Проверяет, находится ли страница в замороженном BFCache-состоянии.
   *
   * @returns {boolean} True, если страница еще не восстановлена из BFCache.
   */
  isSuspended() {
    return this.suspended;
  }

  /**
   * Отмечает начало восстановления страницы из BFCache.
   */
  resume() {
    this.suspended = false;
    this.suppressExpectedDisconnects();
  }

  /**
   * Завершает восстановление после повторного login/subscribe.
   */
  finishResume() {
    this.resumeInProgress = false;
  }

  /**
   * Проверяет, идет ли BFCache-переход или еще действует окно подавления
   * отложенных disconnect-ошибок от старого WebSocket.
   *
   * @returns {boolean} True, если ожидаемые BFCache disconnect можно скрывать.
   */
  isTransitionActive() {
    return this.suspended || this.resumeInProgress || Date.now() < this.suppressDisconnectUntil;
  }

  /**
   * Продлевает окно, в котором ожидаемый disconnect от старого WebSocket
   * не должен попадать в пользовательский error panel.
   */
  suppressExpectedDisconnects() {
    this.suppressDisconnectUntil = Date.now() + DISCONNECT_SUPPRESSION_MS;
  }

  /**
   * Проверяет, является ли ошибка ожидаемым закрытием WebSocket из-за BFCache.
   *
   * @param {Error|string} err Ошибка от Tinode SDK или текст ошибки.
   * @returns {boolean} True, если ошибку можно безопасно скрыть.
   */
  isExpectedDisconnect(err) {
    const message = typeof err == 'string' ? err : err && err.message;
    return (
      this.isTransitionActive() &&
      err &&
      (err.code === 418 || (message && message.indexOf('Disconnected by client') >= 0))
    );
  }
}
